import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Building, Eye, TrendingUp, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useSearchParams } from "react-router-dom";
import PropertyManagement from "../components/PropertyManagement";
import LandlordNotifications from "../components/LandlordNotifications";
import AdminNotifications from "../components/AdminNotifications";
import OwnerHeader from "../components/owner/OwnerHeader";
import RouteGuard from "../components/RouteGuard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useToast } from "@/hooks/use-toast";

const OwnerDashboardContent = () => {
  const { currentUser } = useCurrentUser();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [properties, setProperties] = useState<any[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    occupied: 0,
    totalViews: 0
  });
  const [loading, setLoading] = useState(true);
  const [landlordId, setLandlordId] = useState<string>('');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [subscription, setSubscription] = useState<any>(null);
  
  // Get tab from URL params
  const currentTab = searchParams.get('tab') || 'properties';

  const fetchLandlordData = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get landlord profile with active status and verification
      const { data: landlordData } = await supabase
        .from('landlords')
        .select('id, is_active, is_verified')
        .eq('user_id', user.id)
        .maybeSingle();

      if (landlordData) {
        setLandlordId(landlordData.id);
        // المستخدم نشط فقط إذا كان is_active = true
        setIsActive(landlordData.is_active || false);

        // Get active subscription
        const { data: subscriptionData } = await supabase
          .from('subscriptions')
          .select('*, landlords!inner(full_name)')
          .eq('landlord_id', landlordData.id)
          .eq('status', 'active')
          .gte('end_date', new Date().toISOString().split('T')[0])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        setSubscription(subscriptionData);

        // جلب العقارات دائماً (للعرض فقط) بغض النظر عن حالة التفعيل
        const { data, error } = await supabase
          .from("properties")
          .select("*")
          .eq("owner_id", user.id)
          .order("created_at", { ascending: false });
          
        if (!error) {
          setProperties(data || []);
          
          // حساب الإحصائيات
          const total = data?.length || 0;
          const active = data?.filter(p => p.is_active).length || 0;
          const occupied = data?.filter(p => p.occupied).length || 0;
          const totalViews = data?.reduce((sum, p) => sum + p.views, 0) || 0;
          
          setStats({ total, active, occupied, totalViews });
        }
      }
    } catch (error) {
      console.error('Error fetching landlord data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLandlordData();
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const StatCard = ({ title, value, icon: Icon, color }: any) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 mb-1">{title}</p>
            <p className="text-3xl font-bold text-gray-900">{value}</p>
          </div>
          <div className={`p-3 rounded-full ${color}`}>
            <Icon className="h-6 w-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f1f5f9] to-[#e0e7ef] font-['Tajawal',sans-serif]">
      <OwnerHeader onLogout={handleLogout} landlordId={landlordId} />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* الترحيب والإحصائيات */}
          <div className="mb-8">
            <Card className="mb-6">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="text-right">
                    <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                      مرحباً بك في لوحة تحكم المالك
                    </CardTitle>
                    {isActive ? (
                      <p className="text-gray-600">
                        إدارة شاملة لعقاراتك مع إحصائيات مفصلة وأدوات متقدمة
                      </p>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-orange-600 font-medium">
                          حسابك في انتظار التفعيل من قبل المشرف
                        </p>
                        <p className="text-gray-600 text-sm">
                          سيتم التواصل معك قريباً لتفعيل حسابك وتحديد الباقة المناسبة
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Badge variant="outline" className="text-blue-600 border-blue-600">
                      مالك عقارات
                    </Badge>
                    {!isActive && (
                      <Badge variant="destructive">
                        غير مفعل
                      </Badge>
                    )}
                    {subscription && (
                      <Badge variant="secondary">
                        باقة {subscription.plan_type === 'basic' ? 'أساسية' : 
                              subscription.plan_type === 'standard' ? 'متوسطة' : 'متقدمة'} 
                        ({subscription.properties_limit} عقار)
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* بطاقات الإحصائيات */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="إجمالي العقارات"
                value={stats.total}
                icon={Building}
                color="bg-blue-600"
              />
              <StatCard
                title="العقارات النشطة"
                value={stats.active}
                icon={TrendingUp}
                color="bg-green-600"
              />
              <StatCard
                title="العقارات المؤجرة"
                value={stats.occupied}
                icon={Users}
                color="bg-orange-600"
              />
              <StatCard
                title="إجمالي المشاهدات"
                value={stats.totalViews}
                icon={Eye}
                color="bg-purple-600"
              />
            </div>
          </div>

          {/* التبويبات الرئيسية */}
          <Tabs value={currentTab} onValueChange={(value) => navigate(`/owner-dashboard?tab=${value}`)} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-3">
              <TabsTrigger value="properties" className="text-right">
                إدارة العقارات
              </TabsTrigger>
              <TabsTrigger value="notifications" className="text-right">
                الإشعارات
              </TabsTrigger>
              <TabsTrigger value="analytics" className="text-right">
                التحليلات
              </TabsTrigger>
            </TabsList>

            {/* تبويب إدارة العقارات */}
            <TabsContent value="properties">
              <PropertyManagement 
                properties={properties} 
                onPropertiesChange={fetchLandlordData}
                subscription={subscription}
                isActive={isActive}
              />
            </TabsContent>

            {/* تبويب الإشعارات */}
            <TabsContent value="notifications">
              <div className="space-y-6">
                {landlordId && (
                  <AdminNotifications userType="landlord" userId={landlordId} />
                )}
                <LandlordNotifications />
              </div>
            </TabsContent>

            {/* تبويب التحليلات */}
            <TabsContent value="analytics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-right">أداء العقارات</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {properties.map(property => (
                        <div key={property.id} className="flex justify-between items-center p-3 bg-gray-50 rounded">
                          <div className="text-right flex-1">
                            <p className="font-medium">{property.title}</p>
                            <p className="text-sm text-gray-600">{property.type} - {property.address}</p>
                          </div>
                          <div className="text-left">
                            <p className="text-lg font-bold text-green-600">{property.views}</p>
                            <p className="text-xs text-gray-500">مشاهدة</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-right">ملخص سريع</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                        <span className="text-right">معدل الإشغال</span>
                        <span className="font-bold text-blue-600">
                          {stats.total > 0 ? Math.round((stats.occupied / stats.total) * 100) : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                        <span className="text-right">العقارات المتاحة</span>
                        <span className="font-bold text-green-600">{stats.total - stats.occupied}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                        <span className="text-right">متوسط المشاهدات</span>
                        <span className="font-bold text-purple-600">
                          {stats.total > 0 ? Math.round(stats.totalViews / stats.total) : 0}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

const OwnerDashboard = () => {
  return (
    <RouteGuard requiredUserType="landlord" redirectTo="/login">
      <OwnerDashboardContent />
    </RouteGuard>
  );
};

export default OwnerDashboard;
